/**
 * Money is stored as INTEGER MINOR UNITS (e.g. cents) and branded so a raw number
 * can never be mistaken for a validated monetary amount. Shared-expense math must
 * never use floats — round at the boundary, compute in integers (ARCHITECTURE §3).
 *
 * Display + parsing mirror the Flutter app's `fromMinorUnit` / `toMinorUnit` helpers so amounts
 * read and round identically across platforms. Keep them in lock-step on any change.
 */
export type Money = number & { readonly __brand: 'Money/minorUnits' };

/** Upper bound on a minor-unit amount (mirrors the Flutter `MAX_MINOR_UNIT` guard). */
export const MAX_MINOR_UNIT = Number.MAX_SAFE_INTEGER;

/**
 * Default locale for currency-SYMBOL resolution — always "en-US", not `navigator.language`.
 * `Intl.NumberFormat` disambiguates USD as "US$" (vs. AUD/CAD/SGD/...) in most non-US locales,
 * so a viewer with e.g. an "en-IN" browser would see "US$5595.68" instead of "$5595.68" while an
 * en-US viewer sees the correct bare symbol. Amounts must render identically for every viewer
 * regardless of their OS locale, so this is pinned rather than derived from the browser.
 */
function defaultLocale(): string {
  return 'en-US';
}

export function money(minorUnits: number): Money {
  if (!Number.isInteger(minorUnits)) {
    throw new Error(`Money must be integer minor units, received ${String(minorUnits)}`);
  }
  return minorUnits as Money;
}

/**
 * Minor units → plain decimal string. Port of the Flutter `fromMinorUnit`: returns the
 * MAGNITUDE only (no sign, no currency symbol, no thousands grouping) — the display layer adds
 * the sign and symbol. `4250 → "42.50"`, `5 → "0.05"`, `100 → "1.00"`.
 */
export function fromMinorUnit(
  minorAmount: number,
  decimalPlaces = 2,
  trimTrailingZeros = false,
): string {
  if (decimalPlaces < 0) throw new Error('Invalid decimal places');
  if (minorAmount > MAX_MINOR_UNIT || minorAmount < -MAX_MINOR_UNIT) {
    throw new RangeError('Amount exceeds allowed monetary range');
  }

  let str = String(Math.abs(Math.trunc(minorAmount)));

  if (decimalPlaces > 0) {
    str = str.padStart(decimalPlaces + 1, '0');
    const intPart = str.slice(0, str.length - decimalPlaces);
    const fracPart = str.slice(str.length - decimalPlaces);
    str = `${intPart}.${fracPart}`;
  }

  if (decimalPlaces > 0 && trimTrailingZeros) {
    str = str.replace(/\.?0+$/, '');
  }

  return str;
}

/**
 * Decimal string → branded minor units. Port of the Flutter `toMinorUnit`: validates the format,
 * rounds HALF UP at `decimalPlaces`, and rejects out-of-range / over-precise input.
 * `"42.50" → 4250`, `"0.005" → 1` (rounds up).
 */
export function toMinorUnit(amountStr: string, decimalPlaces = 2): Money {
  if (decimalPlaces < 0) throw new Error('Invalid decimal places');

  const trimmed = amountStr.trim();
  if (trimmed === '') throw new Error('Amount cannot be empty');
  if (!/^-?(?:\d+|\d*\.\d+)$/.test(trimmed)) throw new Error('Invalid money format');

  const negative = trimmed.startsWith('-');
  const normalized = negative ? trimmed.slice(1) : trimmed;

  const parts = normalized.split('.');
  const rawInt = parts[0] ?? '';
  const intPart = rawInt === '' ? '0' : rawInt;
  let fracPart = parts[1] ?? '';

  if (fracPart.length > decimalPlaces + 1) throw new Error('Too many decimal places');
  fracPart = fracPart.padEnd(decimalPlaces + 1, '0');

  const roundingDigit = fracPart.charCodeAt(decimalPlaces) - 48;
  let minor = BigInt(intPart + fracPart.slice(0, decimalPlaces));
  if (roundingDigit >= 5) minor += 1n;
  if (negative) minor = -minor;

  if (minor === 0n) return money(0);
  if (minor > BigInt(MAX_MINOR_UNIT) || minor < BigInt(-MAX_MINOR_UNIT)) {
    throw new RangeError('Amount exceeds allowed monetary range');
  }
  return money(Number(minor));
}

/**
 * Major-unit value → branded minor units. User-entered STRINGS go through the strict, Flutter-
 * matching `toMinorUnit` (validates + rounds half up). Backend NUMBERS use a float-safe round so
 * tiny IEEE-754 artifacts (e.g. `42.500000001`) never throw.
 */
export function moneyFromMajor(major: string | number): Money {
  if (typeof major === 'number') {
    if (!Number.isFinite(major)) throw new Error(`Invalid amount: ${String(major)}`);
    return money(Math.round(major * 100));
  }
  return toMinorUnit(major);
}

const COMMON_CURRENCY_SYMBOLS: Record<string, string> = {
  KWD: 'د.ك',
  AED: 'د.إ',
  SAR: 'ر.س',
  BHD: 'د.ب',
  OMR: 'ر.ع.',
  QAR: 'ر.ق',
  EGP: 'E£',
  JOD: 'د.ا',
  LBP: 'ل.ل',
};

/** Currency symbol for an ISO code in a locale (e.g. `USD → "$"`, `INR → "₹"`, `KWD → "د.ك"`). */
export function currencySymbol(currency: string, locale: string = defaultLocale()): string {
  if (!currency) return '';
  const codeUpper = currency.toUpperCase();
  if (COMMON_CURRENCY_SYMBOLS[codeUpper]) {
    return COMMON_CURRENCY_SYMBOLS[codeUpper];
  }
  try {
    const parts = new Intl.NumberFormat(locale, { style: 'currency', currency }).formatToParts(0);
    const sym = parts.find((p) => p.type === 'currency')?.value;
    if (sym && sym !== codeUpper) {
      return sym;
    }
    return COMMON_CURRENCY_SYMBOLS[codeUpper] ?? sym ?? currency;
  } catch {
    return COMMON_CURRENCY_SYMBOLS[codeUpper] ?? currency;
  }
}

/** Formats integer string with commas in Indian numbering system (e.g. 1,00,000 / 15,420 / 1,000). */
export function formatIntegerWithCommas(intStr: string): string {
  if (!intStr || intStr.length <= 3) return intStr;
  const last3 = intStr.slice(-3);
  const rest = intStr.slice(0, -3);
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
}

/** Formats a decimal string like "10000.00" with commas -> "10,000.00" or "100000.00" -> "1,00,000.00". */
export function formatDecimalWithCommas(decimalStr: string): string {
  const [intPart = '', fracPart] = decimalStr.split('.');
  const formattedInt = formatIntegerWithCommas(intPart);
  return fracPart !== undefined ? `${formattedInt}.${fracPart}` : formattedInt;
}

/** Formats minor unit with commas, e.g. 1000000 -> "10,000.00" or 10000000 -> "1,00,000.00". */
export function fromMinorUnitFormatted(
  minorAmount: number,
  decimalPlaces = 2,
  trimTrailingZeros = false,
): string {
  const raw = fromMinorUnit(minorAmount, decimalPlaces, trimTrailingZeros);
  return formatDecimalWithCommas(raw);
}

/**
 * Format branded minor units for display: `[-]<symbol><amount>` with the amount produced by
 * `fromMinorUnitFormatted` (fixed 2 decimals with comma grouping).
 */
export function formatMoney(
  amount: Money,
  currency: string,
  locale: string = defaultLocale(),
): string {
  const sign = amount < 0 ? '-' : '';
  return `${sign}${currencySymbol(currency, locale)}${fromMinorUnitFormatted(Math.abs(amount))}`;
}
